import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DialogActions, Modal, ModalBody, ModalFooter, ModalHeader, Select } from '../ui';
import type { ActionDescriptor } from '../ui';
import { deleteRole, accessKeys, type Role } from '../../lib/api/access.gen';
import { useCapabilities } from '../../lib/capabilities';
import { errorMessage, errorReason, errorBody } from '../../lib/errorMessage';

// Role delete confirmation (story 2-84, P01 §228: "Role deletion requires
// zero members or an explicit reassignment target (dialog with member
// count)."). Composed entirely from existing DS primitives (Modal family,
// DialogActions, Select) — no new primitive, so this does NOT get its own
// `/app/design` gallery entry, matching `ShareLinksPanel.tsx`'s
// `RevokeConfirmDialog` and `DesignSystemPage.tsx`'s own recorded precedent
// ("no new primitive... no new gallery ENTRY was needed").
//
// Two-step flow, because the TRUE member count is a server fact, not a
// client-derived one: `members` on this page is a cursor PAGE (default 50),
// so counting role holders against only the loaded page would undercount for
// an org past that page size. Step 1 always attempts the plain delete; the
// backend is the one that knows the real count, and answers 409 ROLE_IN_USE
// with it when the role is in use (`orgAccess.service.js#deleteRole`). Step 2
// then renders that real count and a kind-matched reassignment-target picker,
// and retries with `reassignTo`. A role with zero holders never advances past
// step 1 — the plain delete just succeeds.
interface DeleteRoleDialogProps {
  orgId: string;
  role: Role;
  /** Candidate reassignment targets — same-kind roles other than the one being deleted. */
  roles: Role[];
  onClose: () => void;
}

export function DeleteRoleDialog({ orgId, role, roles, onClose }: DeleteRoleDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const { refetch: refetchCapabilities } = useCapabilities();
  const [memberCount, setMemberCount] = useState<number | null>(null);
  const [reassignTo, setReassignTo] = useState('');

  const afterDelete = async () => {
    await Promise.all([queryClient.invalidateQueries({ queryKey: accessKeys.all }), refetchCapabilities()]);
    onClose();
  };

  const mutation = useMutation({
    mutationFn: (body: { reassignTo?: string }) => deleteRole(orgId, role.id, body),
    onSuccess: afterDelete,
    onError: (err) => {
      if (errorReason(err) === 'ROLE_IN_USE') {
        const body = errorBody(err) as { memberCount?: number } | undefined;
        setMemberCount(typeof body?.memberCount === 'number' ? body.memberCount : 0);
      }
    },
  });

  const reassignOptions = roles.filter((r) => r.kind === role.kind && r.id !== role.id);
  const inUse = memberCount !== null && memberCount > 0;

  const actions: ActionDescriptor[] = [
    { id: 'cancel', label: 'Cancel', variant: 'secondary', onClick: onClose, disabled: mutation.isPending },
    {
      id: 'delete',
      label: inUse ? 'Reassign & delete' : 'Delete role',
      variant: 'destructive',
      onClick: () => mutation.mutate(inUse ? { reassignTo } : {}),
      loading: mutation.isPending,
      disabled: mutation.isPending || (inUse && !reassignTo),
      pinned: true,
    },
  ];

  return (
    <Modal onClose={onClose} label="Delete role" disableOutsideDismiss>
      <ModalHeader title={`Delete “${role.name}”?`} onClose={onClose} />
      <ModalBody>
        {!inUse ? (
          <p className="text-sm text-muted-foreground">
            This role has no members. Deleting it cannot be undone.
          </p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {memberCount} member{memberCount === 1 ? ' holds' : 's hold'} this role. Choose a role to move
              {memberCount === 1 ? ' them' : ' them'} to before it can be deleted.
            </p>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground" htmlFor="reassign-role">
                Reassign members to
              </label>
              <Select
                id="reassign-role"
                className="w-full text-sm"
                value={reassignTo}
                onChange={(e) => setReassignTo(e.target.value)}
              >
                <option value="">Select a role…</option>
                {reassignOptions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                    {r.isSystem ? '' : ' (custom)'}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        )}
        {mutation.isError && errorReason(mutation.error) !== 'ROLE_IN_USE' && (
          <p className="mt-2 text-sm text-destructive">{errorMessage(mutation.error)}</p>
        )}
      </ModalBody>
      <ModalFooter>
        <DialogActions actions={actions} />
      </ModalFooter>
    </Modal>
  );
}
